import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import Layout from './components/Layout';
import DynamicPage from './pages/DynamicPage';
import UserPage from './pages/UserPage';
import SubmitPage from './pages/SubmitPage';
import SubmissionsPage from './pages/SubmissionsPage';
import AskPage from './pages/AskPage';
import QuestionsPage from './pages/QuestionsPage';
import AnswersPage from './pages/AnswersPage';
import ReviewPage from './pages/ReviewPage';
import ReviewSubmissionPage from './pages/ReviewSubmissionPage';
import ReviewSummaryPage from './pages/ReviewSummaryPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminStatsPage from './pages/AdminStatsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminTracksPage from './pages/AdminTracksPage';
import AdminQuestionsPage from './pages/AdminQuestionsPage';
import AdminContentPage from './pages/AdminContentPage';
import AssignReviewsPage from './pages/AssignReviewsPage';
import { PageCacheProvider } from './contexts/PageCacheContext';
import { SecurityProvider } from './contexts/SecurityContext';
import ProtectedRoute from './components/security/ProtectedRoute';

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkUserAuth();
  }, []);

  async function checkUserAuth() {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
    }
  }

  async function signOut() {
    try {
      await Auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out: ' + error.message);
    }
  }

  return (
    <SecurityProvider>
      <PageCacheProvider>
        <Router>
          <Layout user={user} onSignOut={signOut}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<DynamicPage pageId="home" />} />
              <Route path="/page/:pageId" element={<DynamicPage />} />
              <Route path="/page/*" element={<DynamicPage />} />
              <Route path="/tracks/answers" element={<AnswersPage />} />
              
              {/* User routes - require authentication */}
              <Route path="/user" element={
                <ProtectedRoute>
                  <UserPage />
                </ProtectedRoute>
              } />
              <Route path="/user/submit" element={
                <ProtectedRoute>
                  <SubmitPage />
                </ProtectedRoute>
              } />
              <Route path="/user/submissions" element={
                <ProtectedRoute>
                  <SubmissionsPage />
                </ProtectedRoute>
              } />
              <Route path="/user/ask" element={
                <ProtectedRoute>
                  <AskPage />
                </ProtectedRoute>
              } />
              <Route path="/user/questions" element={
                <ProtectedRoute>
                  <QuestionsPage />
                </ProtectedRoute>
              } />
              
              {/* Review routes - require reviewer or admin role */}
              <Route path="/review" element={
                <ProtectedRoute requiredRoles={['reviewer', 'admin']}>
                  <ReviewPage />
                </ProtectedRoute>
              } />
              <Route path="/review/submission/:submissionId" element={
                <ProtectedRoute requiredRoles={['reviewer', 'admin']}>
                  <ReviewSubmissionPage />
                </ProtectedRoute>
              } />
              <Route path="/review/summary" element={
                <ProtectedRoute requiredRole="admin">
                  <ReviewSummaryPage />
                </ProtectedRoute>
              } />
              
              {/* Admin routes - require admin role */}
              <Route path="/admin" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/stats" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminStatsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminUsersPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/tracks" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminTracksPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/review" element={
                <ProtectedRoute requiredRole="admin">
                  <AssignReviewsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/questions" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminQuestionsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/content" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminContentPage />
                </ProtectedRoute>
              } />
            </Routes>
          </Layout>
        </Router>
      </PageCacheProvider>
    </SecurityProvider>
  );
}

export default App;
